import { useState, useEffect } from "react";
import { dms } from "./api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RotateCcw } from "lucide-react";
import { niceDate as niceDateRet } from "./api";


const money = (v) => "\u20B9 " + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function ReturnDetailDialog({ entry, onClose, debtorView = false }) {
  const [ret, setRet] = useState(null);
  const [val, setVal] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setRet(null); setVal(null); setErr(""); setLoading(true);
    dms.getReturn(entry.reference_id)
      .then((res) => setRet(res && res.items ? res : (res && res.data) || res))
      .catch((e) => setErr(e.response?.data?.detail || "Return details are not available for this entry"))
      .finally(() => setLoading(false));
    dms.getReturnValue(entry.reference_id).then((v) => setVal(v)).catch(() => setVal(null));
  }, [entry]);

  if (!entry) return null;
  const items = ret?.items || [];
  const vlines = val?.lines || [];
  const total = entry.amount != null ? Number(entry.amount) : (val && val.total != null ? Number(val.total) : vlines.reduce((a, l) => a + Number(l.amount || 0), 0));
  const levelLabel = ret?.level === "primary" ? "Distributor \u2192 Company" : ret?.level === "secondary" ? "Retailer \u2192 Distributor" : "";

  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Return {entry.reference_no}</DialogTitle></DialogHeader>
        {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading...</div>}
        {err && (
          <div className="text-sm text-slate-600 py-4">
            <div>{err}</div>
            {entry.description && <div className="text-xs text-slate-500 mt-1">{entry.description}</div>}
          </div>
        )}
        {ret && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Order returned</div><div className="font-semibold text-slate-900">{ret.order_no || "\u2014"}</div></div>
              <div><div className="text-xs text-slate-500">Return type</div><div className="font-semibold text-slate-900">{levelLabel || "\u2014"}</div></div>
              <div><div className="text-xs text-slate-500">Requested by</div><div className="font-semibold text-slate-900">{ret.created_by_name || "\u2014"}</div></div>
              <div><div className="text-xs text-slate-500">Status</div><div className="font-semibold text-slate-900 capitalize">{ret.status}</div></div>
              <div><div className="text-xs text-slate-500">Requested on</div><div className="text-slate-900">{niceDateRet(ret.created_at)}</div></div>
              <div><div className="text-xs text-slate-500">Approved on</div><div className="text-slate-900">{ret.decided_at ? niceDateRet(ret.decided_at) : "\u2014"}</div></div>
            </div>
            <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs uppercase text-slate-500 bg-slate-50">
                <div className="col-span-5">Product</div>
                <div className="col-span-2 text-right">Quantity</div>
                <div className="col-span-2 text-right">Rate / pc</div>
                <div className="col-span-3 text-right">Amount</div>
              </div>
              {items.map((it, i) => {
                const vl = vlines[i];
                return (
                  <div key={(it.product_id || "") + i} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm">
                    <div className="col-span-5">
                      <div className="font-medium text-slate-900">{it.product_name}</div>
                      <div className="text-[10px] font-mono text-slate-500">{it.sku_code}</div>
                      {it.reason && <div className="text-[11px] text-slate-500">Reason: {it.reason}</div>}
                    </div>
                    <div className="col-span-2 text-right font-semibold text-slate-900">
                      {Number(it.qty_boxes || 0) > 0 && <div>{Number(it.qty_boxes)} boxes</div>}
                      {Number(it.qty_pcs || 0) > 0 && <div>{Number(it.qty_pcs)} pcs</div>}
                      {!(Number(it.qty_boxes || 0) > 0) && !(Number(it.qty_pcs || 0) > 0) && <div>{Number(it.total_box_equiv || 0)} boxes</div>}
                      {vl && Number(it.qty_boxes || 0) > 0 && <div className="text-[11px] font-normal text-slate-500">{vl.qty_pcs} pcs in total</div>}
                    </div>
                    <div className="col-span-2 text-right text-slate-700">{vl ? money(vl.rate_pc) : "\u2014"}</div>
                    <div className="col-span-3 text-right font-semibold text-slate-900">{vl ? money(vl.amount) : "\u2014"}</div>
                  </div>
                );
              })}
              <div className="grid grid-cols-12 gap-2 px-3 py-3 bg-slate-50">
                <div className="col-span-9 text-sm font-semibold text-slate-900">Final total (incl. GST)</div>
                <div className="col-span-3 text-right text-lg font-bold text-emerald-700">{debtorView ? "+" : "-"}{money(total)}</div>
              </div>
            </div>
            {ret.notes && <div className="text-xs text-slate-600">Notes: {ret.notes}</div>}
            {ret.decision_notes && <div className="text-xs text-slate-600">Decision notes: {ret.decision_notes}</div>}
          </div>
        )}
        {!ret && !loading && (
          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <div className="text-xs text-slate-500">Ledger amount (incl. GST)</div>
            <div className="text-lg font-bold text-emerald-700">{debtorView ? "+" : "-"}{money(total)}</div>
          </div>
        )}
        <div className="flex justify-end"><Button variant="outline" onClick={onClose}>Close</Button></div>
      </DialogContent>
    </Dialog>
  );
}

function SimpleLedgerDialog({ entry, onClose, debtorView = false }) {
  if (!entry) return null;
  if (entry.kind === "sale_return" && entry.reference_id && String(entry.reference_no || "").startsWith("RET")) {
    return <ReturnDetailDialog entry={entry} onClose={onClose} debtorView={debtorView} />;
  }
  const LABELS = { invoice: "Sale / Invoice", payment: "Payment", sale_return: "Sale Return", credit_note: "Credit Note", debit_note: "Debit Note", coupon_credit: "Coupon Credit" };
  const rows = [
    ["Type", LABELS[entry.kind] || entry.kind],
    ["Number", entry.reference_no || "\u2014"],
    ["Date", entry.at ? niceDateRet(entry.at) : "\u2014"],
    ["Amount", money(entry.amount)],
  ];
  if (entry.status) rows.push(["Status", String(entry.status)]);
  if (entry.method) rows.push(["Method", String(entry.method).replace(/_/g, " ")]);
  if (entry.description) rows.push(["Details", entry.description]);
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{LABELS[entry.kind] || entry.kind} {entry.reference_no}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-2">
              <span className="text-slate-500">{k}</span>
              <span className="font-medium text-slate-900 text-right">{v}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceEntryDialog({ entry, onClose, debtorView = false }) {
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setInv(null); setFailed(false); setLoading(true);
    const call = entry.retailer_id ? dms.printRetailerBill : dms.printEbill;
    call(entry.reference_id)
      .then((d) => { if (d && d.invoice && (d.invoice.items || []).length) setInv(d.invoice); else setFailed(true); })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [entry]);
  if (failed) return <SimpleLedgerDialog entry={entry} onClose={onClose} debtorView={debtorView} />;
  const t = (inv && inv.totals) || {};
  const items = (inv && inv.items) || [];
  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle>Invoice {(inv && inv.doc_no) || entry.reference_no}</DialogTitle></DialogHeader>
        {loading && <div className="text-sm text-slate-500 py-6 text-center">Loading...</div>}
        {inv && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><div className="text-xs text-slate-500">Date</div><div className="font-semibold text-slate-900">{inv.date ? niceDateRet(inv.date) : (entry.at ? niceDateRet(entry.at) : "\u2014")}</div></div>
              <div><div className="text-xs text-slate-500">Billed to</div><div className="font-semibold text-slate-900">{(inv.bill_to && (inv.bill_to.name || inv.bill_to.business_name)) || "\u2014"}</div></div>
            </div>
            <div className="border border-slate-200 rounded divide-y divide-slate-100 max-h-[40vh] overflow-y-auto">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs uppercase text-slate-500 bg-slate-50">
                <div className="col-span-4">Product</div>
                <div className="col-span-2 text-right">Quantity</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-1 text-right">GST</div>
                <div className="col-span-3 text-right">Amount</div>
              </div>
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 text-sm">
                  <div className="col-span-4">
                    <div className="font-medium text-slate-900">{it.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">{it.sku_code}</div>
                  </div>
                  <div className="col-span-2 text-right font-semibold text-slate-900">{it.qty_label}</div>
                  <div className="col-span-2 text-right text-slate-700">{money(it.rate)}</div>
                  <div className="col-span-1 text-right text-slate-700">{Number(it.gst_pct || 0)}%</div>
                  <div className="col-span-3 text-right font-semibold text-slate-900">{money(it.amount)}</div>
                </div>
              ))}
              <div className="px-3 py-3 bg-slate-50 space-y-1 text-sm">
                {t.subtotal != null && <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{money(t.subtotal)}</span></div>}
                {t.gst_total != null && <div className="flex justify-between"><span className="text-slate-500">GST</span><span>{money(t.gst_total)}</span></div>}
                {Number(t.trade_discount_total || 0) > 0 && <div className="flex justify-between"><span className="text-slate-500">Trade discount</span><span>-{money(t.trade_discount_total)}</span></div>}
                {Number(t.round_off || 0) !== 0 && <div className="flex justify-between"><span className="text-slate-500">Round off</span><span>{money(t.round_off)}</span></div>}
                {t.grand_total != null && <div className="flex justify-between font-semibold text-slate-900"><span>Invoice total</span><span>{money(t.grand_total)}</span></div>}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <div className="text-xs text-slate-500">Ledger amount</div>
          <div className="text-lg font-bold text-slate-900">{debtorView ? "-" : "+"}{money(entry.amount)}</div>
        </div>
        <div className="flex justify-end"><Button variant="outline" onClick={onClose}>Close</Button></div>
      </DialogContent>
    </Dialog>
  );
}

export function LedgerEntryDialog({ entry, onClose, debtorView = false }) {
  if (!entry) return null;
  if (entry.kind === "invoice" && entry.reference_id) {
    return <InvoiceEntryDialog entry={entry} onClose={onClose} debtorView={debtorView} />;
  }
  return <SimpleLedgerDialog entry={entry} onClose={onClose} debtorView={debtorView} />;
}
